import {
	DataSource,
	InfiniteTable,
	type InfiniteTableColumn,
} from "@infinite-table/infinite-react";
import type { Pead } from "epsm-api-types/models";
import { INFINITY_TABLE } from "@/config";
import { settings } from "@/lib/utils/settings/settings";
import { getAccuracyDriftForHistory } from "../../../lib/helpers/getAccuracyDriftForHistory/getAccuracyDriftForHistory";
import type { PeadFilterType } from "../../../types";
import "../../Pead.css";
import { formatValuePercent } from "@/components/widgets/helpers/FormattingHelpers";
import { getActualDataForHistory } from "../../../lib/helpers/getActualDataForHistory";
import { getForecastResult } from "../../../lib/helpers/getForecastResult/getForecastResult";
import { getQuarter } from "../../../lib/helpers/getQuarter/getQuarter";

type Props = {
	ticker: string;
	data: Pead[];
	setActiveChart: (chart: Pead) => void;
	peadFilter: PeadFilterType;
};

const baseColumnConfig: Partial<InfiniteTableColumn<Pead>> = {
	defaultFilterable: false,
	defaultSortable: false,
	align: "center",
	defaultFlex: 1,
	maxWidth: 220,
};

export const columns: Record<string, InfiniteTableColumn<Pead>> = {
	qtr: {
		...baseColumnConfig,
		field: "overall_count",
		header: "Qtr",
		defaultWidth: 40,
		valueGetter: getQuarter,
		headerClassName: "history-header history-qtr-cell",
		className: "history-qtr-cell",
	},
	drift: {
		...baseColumnConfig,
		valueGetter: getPeadType,
		header: "Drift",
		headerClassName: "history-header",
	},
	sample: {
		...baseColumnConfig,
		header: "Sample",
		valueGetter: ({ data }) => `${data.sample_count}/${data.overall_count}`,
		headerClassName: "history-header",
	},
	accuDriftEnd: {
		...baseColumnConfig,
		header: "Accy Drift end",
		headerClassName: "history-header",
		valueGetter: getAccuracyDriftForHistory,
	},
	inOut: {
		...baseColumnConfig,
		header: "In/Out",
		valueGetter: getForecastResult,
		headerClassName: "history-header",
	},
	accuDriftAll: {
		...baseColumnConfig,
		header: "Accy all end",
		valueGetter: (data) =>
			getAccuracyDriftForHistory({ ...data, driftEnd: false }),
		headerClassName: "history-header",
	},

	forecast: {
		...baseColumnConfig,
		header: "Forecast",
		headerClassName: "history-header",
		valueGetter: getForecast,
	},
	actual: {
		...baseColumnConfig,
		header: "Actual",
		headerClassName: "history-header",
		valueGetter: ({ data }) => getActualDataForHistory(data),
		defaultWidth: 50,
	},
};

export const PeadHistory: React.FC<Props> = ({
	ticker,
	data,
	setActiveChart,
}) => {
	return (
		<div className="w-full h-full history" data-testid="pead-history">
			<h1>PEAD History {ticker}</h1>

			<div className="border-2 border-gray w-full h-[400px]">
				<DataSource<Pead> data={data} primaryKey="ticker">
					<InfiniteTable<Pead>
						columns={columns}
						columnDefaultWidth={150}
						licenseKey={settings.infiniteTableLicenseKey}
						rowHeight={INFINITY_TABLE.ROW_HEIGHT}
						onCellClick={(cellData) => setActiveChart(data[cellData.rowIndex])}
					/>
				</DataSource>
			</div>
		</div>
	);
};

export function getPeadType({ data }: { data: Pead }) {
	return ["1", "2", "3", "4"].includes(data.drift_unit.value)
		? `Q${data.drift_unit.value}`
		: data.drift_unit.value;
}

export function getForecast({ data }: { data: Pead }) {
	return formatValuePercent(data.forecast, "N/A");
}
